/**
 * The `rq*` URL contract for the two Required-action tabs — reading a link, and
 * building the query to write back.
 *
 * Every panel on this route is rendered with `&&`, so a tab switch UNMOUNTS it
 * (`ReconciliationV2.tsx`). Benefits and Transaction Details survive that
 * because they round-trip their state through the URL; this module is the same
 * trick for Unallocated payments and Discrepancies, which used to lose filter,
 * search and page on every switch. The URL also survives a browser reload and
 * makes the view shareable, which a keep-mounted fix would not (spec D6).
 *
 * Filter, search and page are held PER TAB (spec D7): the two filter
 * vocabularies are disjoint apart from 'all', so one shared key could only ever
 * preserve 'all'. Hence two disjoint key namespaces — `rqTx*` and `rqDisc*` —
 * each panel owning exactly its own three keys so that its write cannot clear
 * its sibling's state.
 *
 * Page size is deliberately NOT here. Both sibling panels keep `limit`
 * component-local, and so does this one (spec D6).
 *
 * `rqOpen` is the odd one out: a ONE-SHOT deep link to a single row, owned by
 * neither panel, consumed and cleared on open. This module both reads it — as a
 * bare transaction id or a `benefitId:periodStartDate:issueType` composite,
 * disambiguated by which `tab` is open — and builds the shareable link that
 * carries it.
 *
 * Kept free of React and of the api-client (which instantiates axios at load),
 * matching ./helpers — the parsing rules are the interesting part and they unit
 * -test in isolation.
 */

import { ReconIssueType } from '../../../api/reconciliation-v2-enums'
import type { ReviewQueueKind } from '../../../api/reconciliation-v2-enums'
import {
  DISCREPANCY_FILTER_OPTIONS,
  TRANSACTION_FILTER_OPTIONS,
} from './helpers'
import type { DiscrepancyFilter, TransactionFilter } from './helpers'

/**
 * The parent route's `tab` value that mounts each arm of the queue.
 *
 * This REPLACED an `rqKind` param of our own (EMS-10248): the two arms are
 * top-level tabs now, so the route's own `tab` already says which one is open
 * and a second key saying the same thing could only ever disagree with it.
 *
 * `tab` belongs to ReconciliationV2, not to this namespace — but a link that
 * does not carry it lands the recipient on Benefits with an `rqOpen` nothing
 * reads. The values are spelt here rather than threaded down from the page
 * because this module is where the whole shareable link is assembled, and
 * because the page reads them back to decide which arm to mount.
 */
export const QUEUE_TAB = {
  transaction: 'unallocated',
  discrepancy: 'flagged',
} as const satisfies Record<ReviewQueueKind, string>

export type QueueTab = (typeof QUEUE_TAB)[ReviewQueueKind]

/** The arm a `tab` value names, or `undefined` when it names neither. */
export const kindOfTab = (tab: unknown): ReviewQueueKind | undefined =>
  (Object.keys(QUEUE_TAB) as ReviewQueueKind[]).find(
    (kind) => QUEUE_TAB[kind] === tab
  )

/**
 * One-shot deep link to a single row, read against whichever panel is
 * mounted — which the route's `tab` is what selects ({@link QUEUE_TAB}).
 *
 * Owned by NOBODY: neither panel lists it in `ownedKeys`, so an ordinary
 * filter/search/page sync cannot wipe a link that has not been consumed yet.
 * The panel that consumes it clears it alongside the state it seeded,
 * mirroring `tdOpen` (TransactionDetailsPanel) and the `adj*` one-shots
 * (BenefitsTable).
 *
 * Because the reader is the mounted panel rather than the key itself, the PAGE
 * also drops an unconsumed one on a manual tab switch — otherwise a discrepancy
 * composite left behind by a failed request would be read by the transaction
 * arm, which takes any string verbatim. See ReconciliationV2's `setActiveTab`;
 * that owner moved up from the sub-tab shell when the arms became page tabs,
 * and the drop must move with it or the bug returns.
 */
export const RQ_OPEN_KEY = 'rqOpen'

/** Separates the three parts of a discrepancy `rqOpen`. See {@link readOpenTarget}. */
const COMPOSITE_SEPARATOR = ':'

/**
 * Omitted from the URL. Exported only for the equality pin in
 * ./url-persistence.test.tsx: must stay equal to `REVIEW_QUEUE_DEFAULTS.page` in the
 * api-client — a panel mounting at its default page has to hash to the same
 * React Query key as the count call, or the count in the tab label stops
 * sharing a request with the list. Not imported from there because that module
 * loads axios; the equality is pinned by a test in ./url-persistence.test.tsx.
 */
export const DEFAULT_PAGE = 1

export interface PanelUrlState<F extends string> {
  filter: F
  /** The APPLIED search — what has been submitted, not what is being typed. */
  search: string
  page: number
}

/**
 * One tab's slice of the URL, plus the arm of the queue it is a view of.
 *
 * `kind` lives here rather than beside it as a second argument so the request
 * param and the key namespace cannot disagree: a panel asking for
 * `kind=transaction` while reading and writing `rqDisc*` would still get a 200
 * back, and only its persistence would be wrong — which is precisely the class
 * of bug this module exists to prevent.
 */
export interface PanelUrlContract<F extends string> {
  /** The arm of the UNION this tab is a view of — the EPS `kind` param. */
  kind: ReviewQueueKind
  keys: {
    readonly filter: string
    /** Spelt `…Q` on the wire, matching the `td*` / Benefits precedent. */
    readonly search: string
    readonly page: string
  }
  /**
   * Every key this panel owns, for `replaceOwnedQuery`. Exactly its own three —
   * never the whole `rq*` namespace, which would wipe the sibling tab's state
   * (and `rqOpen`, the one-shot deep link) on any write.
   */
  ownedKeys: readonly string[]
  /**
   * Legal values for this tab's `filter` key, derived from the chips it
   * actually renders so a new chip is URL-persistable without a second edit.
   *
   * This is the validation the spec calls for. EPS answers a `filter` that does
   * not belong to the requested `kind` with a 400, and `ReviewQueueFilter` is
   * the union of both vocabularies, so the compiler cannot catch a stale or
   * hand-edited `rqDiscFilter=refund`.
   *
   * Note this is deliberately NOT the cross-key check the ticket's prose
   * describes (`rqTxFilter=refund` on the Discrepancies tab). Under namespaced
   * keys that pair is unreachable: each key is only ever read by its owning
   * contract, so the panel the stray value would reach is not mounted.
   * Per-key validation is the strictly stronger check — the two vocabularies
   * are disjoint apart from `all`, so no value can be legal in the wrong key.
   */
  allowedFilters: readonly F[]
  /** Omitted from the URL. Pinned to 'all' for the count-dedupe above. */
  defaultFilter: F & 'all'
}

const contract = <F extends string>(
  kind: ReviewQueueKind,
  keys: PanelUrlContract<F>['keys'],
  filterOptions: readonly { value: F }[]
): PanelUrlContract<F> => ({
  kind,
  keys,
  ownedKeys: [keys.filter, keys.search, keys.page],
  allowedFilters: filterOptions.map((option) => option.value),
  defaultFilter: 'all' as F & 'all',
})

export const TRANSACTION_URL_CONTRACT = contract<TransactionFilter>(
  'transaction',
  { filter: 'rqTxFilter', search: 'rqTxQ', page: 'rqTxPage' },
  TRANSACTION_FILTER_OPTIONS
)

// `rqDisc*`, not `rqIs*`: CONTEXT.md lists "issue" under _Avoid_ for
// Discrepancy, and spec D11 puts new code under that vocabulary. The wire's row
// discriminator stays `'issue'` — that one is EPS's and renaming it is
// breaking — but a brand-new URL key has no such excuse. Not `rqFlag*` either:
// "Flagged issue" is the LABEL of one issue type on this tab, and D11 keeps the
// rename to UI copy.
export const DISCREPANCY_URL_CONTRACT = contract<DiscrepancyFilter>(
  'discrepancy',
  { filter: 'rqDiscFilter', search: 'rqDiscQ', page: 'rqDiscPage' },
  DISCREPANCY_FILTER_OPTIONS
)

/**
 * `location.query` values are `unknown`: react-router hands back an ARRAY for a
 * repeated key (`?rqTxQ=a&rqTxQ=b`), so nothing read out of it may go straight
 * to a string method. Such a key is dropped by `replaceOwnedQuery` on the next
 * write anyway, so treating it as absent is the honest reading.
 */
const readString = (
  query: Record<string, unknown>,
  key: string
): string | undefined => {
  const value = query[key]
  return typeof value === 'string' && value !== '' ? value : undefined
}

/**
 * The row an inbound `rqOpen` points at, once parsed against the arm it
 * belongs to.
 *
 * The discrepancy arm needs all THREE parts: one benefit period can carry both
 * an auto issue (overpaid / underpaid) and a `manual_review` issue as two
 * separate rows, so `benefitId` + `periodStartDate` alone picks the wrong one
 * half the time.
 */
export type ReviewQueueOpenTarget =
  | { kind: 'transaction'; transactionId: string }
  | {
      kind: 'discrepancy'
      benefitId: string
      periodStartDate: string
      issueType: `${ReconIssueType}`
    }

const ISSUE_TYPES: ReadonlySet<string> = new Set(Object.values(ReconIssueType))

/**
 * Read `rqOpen` as a row of THIS tab's arm, or `undefined` when there is no
 * readable deep link for it.
 *
 * Colon-delimited rather than repeated keys on purpose: `replaceOwnedQuery`
 * keeps a foreign key only when it is a non-empty STRING, so an array-valued
 * param is silently dropped on the next write — which is why `td*` comma-joins
 * its multi-values too.
 *
 * A transaction id is taken verbatim, colons and all. The format of an EPS
 * transaction id is not this module's business, and guessing at it would
 * silently kill a legitimate link to save one request on a malformed one — the
 * no-match path in the panel already handles both identically.
 *
 * An unreadable `rqOpen` is not a deep link: nothing neutralises the rest of
 * the namespace for it and nothing consumes it, so it lingers until the next
 * write that owns the key — the panel's own consume, or the page's tab
 * switch. The spec's "omitted when: always after consume" covers a link that
 * WAS consumed; this one never was.
 *
 * The three-part split assumes `periodStartDate` carries no colon. It is
 * documented as `YYYY-MM-DD` on `ReconIssueRow` and is the first day of a
 * coverage month, never a timestamp; were that to change, the producer and
 * this parser would both need a different delimiter.
 */
export const readOpenTarget = <F extends string>(
  query: Record<string, unknown>,
  { kind }: PanelUrlContract<F>
): ReviewQueueOpenTarget | undefined => {
  const raw = readString(query, RQ_OPEN_KEY)
  if (!raw) return undefined
  if (kind === 'transaction') return { kind, transactionId: raw }
  const parts = raw.split(COMPOSITE_SEPARATOR)
  if (parts.length !== 3) return undefined
  const [benefitId, periodStartDate, issueType] = parts
  if (!benefitId || !periodStartDate) return undefined
  if (!ISSUE_TYPES.has(issueType)) return undefined
  return {
    kind,
    benefitId,
    periodStartDate,
    issueType: issueType as `${ReconIssueType}`,
  }
}

/**
 * The search term that puts a deep-linked row on page 1.
 *
 * EPS matches `search` against transaction id / employee / benefit id on the
 * transaction arm, and employee / benefit id / insured-persons JSON on the
 * discrepancy arm — so the benefit id is the only part of the discrepancy
 * composite that narrows anything. Neither period nor issue type is searchable,
 * which is why the panel still has to pick its row out of the result.
 */
const openTargetSearch = (target: ReviewQueueOpenTarget): string =>
  target.kind === 'transaction' ? target.transactionId : target.benefitId

/** The `rq*` query for a shareable link to one row — and nothing else. */
export const buildOpenQuery = (
  target: ReviewQueueOpenTarget
): Record<string, string> => ({
  tab: QUEUE_TAB[target.kind],
  [RQ_OPEN_KEY]:
    target.kind === 'transaction'
      ? target.transactionId
      : [target.benefitId, target.periodStartDate, target.issueType].join(
          COMPOSITE_SEPARATOR
        ),
})

/**
 * Absolute link to one row, for the operator to paste to a colleague.
 *
 * Built from the route and {@link buildOpenQuery} alone — deliberately NOT from
 * the sender's current query, which would carry their Benefits year, their
 * `td*` filters and their own `rqTx*` / `rqDisc*` state into the recipient's
 * session and hide the very row the link is about.
 */
export const rowDeepLinkUrl = (target: ReviewQueueOpenTarget): string => {
  const query = new URLSearchParams(buildOpenQuery(target)).toString()
  const { origin, pathname } = window.location
  return `${origin}${pathname}?${query}`
}

export const readPanelUrlState = <F extends string>(
  query: Record<string, unknown>,
  contract: PanelUrlContract<F>
): PanelUrlState<F> => {
  const { keys, allowedFilters, defaultFilter } = contract
  // A deep link OVERRIDES the rest of this namespace: a shared link is read by
  // someone who set none of it, and a leftover filter or page — theirs or the
  // sender's — would hide the very row the link is about. Same move as the
  // `tdOpen` jump, which clears every other `td*` key and seeds `tdQ` with the
  // id. An unreadable `rqOpen` is not a deep link and neutralises nothing.
  const openTarget = readOpenTarget(query, contract)
  if (openTarget) {
    return {
      filter: defaultFilter,
      search: openTargetSearch(openTarget),
      page: DEFAULT_PAGE,
    }
  }
  const filterParam = readString(query, keys.filter)
  const parsedPage = parseInt(readString(query, keys.page) ?? '', 10)
  return {
    filter:
      filterParam && (allowedFilters as readonly string[]).includes(filterParam)
        ? (filterParam as F)
        : defaultFilter,
    search: readString(query, keys.search) ?? '',
    page:
      Number.isFinite(parsedPage) && parsedPage >= 1
        ? parsedPage
        : DEFAULT_PAGE,
  }
}

export const buildPanelQuery = <F extends string>(
  { filter, search, page }: PanelUrlState<F>,
  { keys, defaultFilter }: PanelUrlContract<F>
): Record<string, string> => {
  const query: Record<string, string> = {}
  if (filter !== defaultFilter) query[keys.filter] = filter
  if (search) query[keys.search] = search
  if (page !== DEFAULT_PAGE) query[keys.page] = String(page)
  return query
}
